<template>
  <card-container
    id="update-partner-communication"
    class="partner-communication"
    :loading-message="status.spinnerMessage"
    :is-loading="status.isLoading"
    @submit="submitForm"
    @reset="resetForm"
  >
    <template #title>
      Settings and Communication
    </template>

    <template #content>
      <h6 class="sub-section-title">
        Remind about scheduled ride
      </h6>

      <div class="row ml-1 mb-30">
        <number-input
          id="scheduledRideReminderMinutesAfterCreation"
          ref="scheduledRideReminderInputElement"
          v-model.number="communicationData.scheduledRideReminderMinutesAfterCreation"
          :allow-null="true"
          :is-decimal="false"
          type="number"
          max="480"
          min="0"
          style="width: 150px"
          @blur="validateScheduledRideReminderMinutesAfterCreation"
        >
          <template #append>
            Minutes
          </template>
        </number-input>
        <span
          v-if="!!invalidErrorMessage"
          class="help-block text-red"
        >
          {{ invalidErrorMessage }}
        </span>
        <div class="help-block">
          When will the first robo-call notification be sent after an order is created?
        </div>
      </div>

      <h6 class="sub-section-title">
        Settings and Communication Information
      </h6>

      <fieldset>
        <div class="row align-items-center mobile-mb-0">
          <div
            v-for="(checkbox, index) in checkboxConfig"
            :key="index"
            class="col-sm-3 col-6 checkbox-container"
          >
            <el-checkbox
              :id="checkbox.id"
              v-model="communicationData[checkbox.model]"
            >
              {{ checkbox.label }}
            </el-checkbox>
          </div>
        </div>
      </fieldset>

      <h6 class="sub-section-title">
        Functional Settings
      </h6>

      <div class="row mb-30">
        <div class="col-sm-3 col-6 checkbox-container">
          <el-checkbox
            id="isScheduledRideRestricted"
            v-model="communicationData.isScheduledRideRestricted"
          >
            Restrict Scheduled Rides
          </el-checkbox>
        </div>
      </div>

      <h6 class="sub-section-title">
        Exclude CRM A/B Test in Partner Members
      </h6>

      <div class="row mb-30">
        <div class="col-sm-6 checkbox-container">
          <el-checkbox
            id="noCrm"
            v-model="noCrm"
          >
            Do not send me CRM messages (email/text/etc)
          </el-checkbox>
          <div class="help-block">
            Note that if this option is enabled, the user will not get any crm campaign items!
          </div>
        </div>
      </div>

      <el-checkbox-group v-model="excludeAbTestTypes">
        <div
          class="row"
          :class="{ 'disabled-container': noCrm }"
        >
          <div
            v-for="(item, index) in crmAbTestTypes"
            :key="index"
            class="col-sm-4 col-6 checkbox-container"
          >
            <el-checkbox :label="item">
              {{ startCase(item) }}
            </el-checkbox>
          </div>
        </div>
      </el-checkbox-group>
    </template>

    <template #submit-button>
      Update
    </template>
  </card-container>
</template>

<script setup lang="ts">
import CardContainer from '@/components/UIComponents/CardContainer.vue';
import { onMounted, reactive, ref, Ref, type ComponentPublicInstance } from 'vue';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import util from '@/Utils/util';
import { cloneDeep, startCase } from 'lodash';
import { updatePartner } from '@/services/partners/updatePartner';
import { abTestService } from '@/services/abTests';

// hooks
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
const { newPartnerInfo, setNewPartnerInfo } = usePartner();

const emit = defineEmits(['goto-next-step']);

// boolean
const noCrm = ref<boolean>(false);

const invalidErrorMessage = ref('');
const scheduledRideReminderInputElement: Ref<ComponentPublicInstance | null> = ref(null);

// arrays
const crmAbTestTypes: Ref<string[]> = ref([]);
const excludeAbTestTypes: any = ref([]);
const initialExcludeAbTestTypes: any = ref([]);
const checkboxConfig = [
  { id: 'noMail', model: 'noMail', label: 'Do not send Mail' },
  { id: 'marketingMail', model: 'marketingMail', label: 'Marketing Mail (physical)' },
  { id: 'transactionalMail', model: 'transactionalMail', label: 'Transactional Mail (physical)' },
  { id: 'marketingEmails', model: 'marketingEmails', label: 'Marketing Emails' }, // marketingEmail ?
  { id: 'transactionalEmails', model: 'transactionalEmails', label: 'Transactional Emails' }, //-
  { id: 'noRobocalls', model: 'noRobocalls', label: 'Do not call me' }, //-
  { id: 'marketingRobocalls', model: 'marketingRobocalls', label: 'Marketing Robocalls' }, // -
  { id: 'transactionalRobocalls', model: 'transactionalRobocalls', label: 'Transactional Robocalls' }, // -
  { id: 'marketingSms', model: 'marketingSms', label: 'Marketing SMS' },
  { id: 'transactionalSms', model: 'transactionalSms', label: 'Transactional SMS' },
  { id: 'isEmailReceipt', model: 'isEmailReceipt', label: 'Receipt Email' },
  {
    id: 'isPartnerWantEmailReceiptToMemberEvenIf100PercentSubsidy',
    model: 'isPartnerWantEmailReceiptToMemberEvenIf100PercentSubsidy',
    label: 'Send Receipt Email to Customer Even If 100% Subsidy',
  },
  { id: 'isNotChargeForCancellationFees', model: 'isNotChargeForCancellationFees', label: 'Not Charge Cancellation Fees' },
  { id: 'isNotCountCanceledRides', model: 'isNotCountCanceledRides', label: 'Not Count Canceled Rides' },
  { id: 'isIncludeInMoveUserIntoPartnershipForm', model: 'isIncludeInMoveUserIntoPartnershipForm', label: 'Move User Into Partnership' },
  { id: 'isAutoConfirmSurge', model: 'isAutoConfirmSurge', label: 'Auto Confirm Surge' },
  { id: 'isHideRegistrationLink', model: 'isHideRegistrationLink', label: 'Hide Registration Link' },
  { id: 'isSendRenewalNotification', model: 'isSendRenewalNotification', label: 'Send Renewal Notification' },
  { id: 'turnOnRideOrderingLPAlert', model: 'turnOnRideOrderingLPAlert', label: 'Turn On Ride Ordering LP Alert' },
  { id: 'isRecordAllCalls', model: 'isRecordAllCalls', label: 'Record All Calls' },
  { id: 'scheduledRideConfirmedEmail', model: 'scheduledRideConfirmedEmail', label: 'Scheduled Ride Confirmed Email' },
  {
    id: 'scheduledRideReminderDayBeforeEmail',
    model: 'scheduledRideReminderDayBeforeEmail',
    label: 'Scheduled Ride Reminder Day Before Email',
  },
  { id: 'scheduledRideCancelledSms', model: 'scheduledRideCancelledSms', label: 'Scheduled Ride Cancelled Sms' },
  { id: 'isSendWelcomeKits', model: 'isSendWelcomeKits', label: 'Send Welcome Kits' },
  {
    id: 'isShowUserPersonalRidesOnPartnerDashboard',
    model: 'isShowUserPersonalRidesOnPartnerDashboard',
    label: 'Show User Personal Rides On Partner Dashboard',
  },
  { id: 'isAlwaysOperator', model: 'isAlwaysOperator', label: 'Turn on Always Connect to a Live Operator' },
  { id: 'allowCallersVoipPhoneNumbers', model: 'allowCallersVoipPhoneNumbers', label: 'Is VOIP Numbers Allowed' },
  { id: 'collectCc', model: 'collectCc', label: "Collect Rider's Credit Card" },
  { id: 'isAllowCustomerTip', model: 'isAllowCustomerTip', label: 'Allow Customer Tip' },
];

// objects
const status: {
  spinnerMessage: string;
  isLoading: boolean;
} = reactive({
  spinnerMessage: '',
  isLoading: false,
});

let communicationData: any = reactive({
  scheduledRideReminderMinutesAfterCreation: undefined,
  isEmailReceipt: false,
  isPartnerWantEmailReceiptToMemberEvenIf100PercentSubsidy: false,
  isNotChargeForCancellationFees: false,
  isNotCountCanceledRides: false,
  isIncludeInMoveUserIntoPartnershipForm: true,
  isHideRegistrationLink: false,
  isSendRenewalNotification: false,
  isRecordAllCalls: false,
  isAutoConfirmSurge: false,
  turnOnRideOrderingLPAlert: false,
  scheduledRideConfirmedEmail: false,
  scheduledRideReminderDayBeforeEmail: false,
  noMail: false,
  noRobocalls: false,
  marketingMail: true,
  isSendWelcomeKits: false,
  isShowUserPersonalRidesOnPartnerDashboard: false,
  isAlwaysOperator: false,
  allowCallersVoipPhoneNumbers: false,
  collectCc: false,
  transactionalMail: true,
  marketingEmails: true,
  transactionalEmails: true,
  marketingRobocalls: true,
  transactionalRobocalls: true,
  transactionalSms: true,
  marketingSms: true,
  isAllowCustomerTip: true,
  scheduledRideCancelledSms: false,
  isScheduledRideRestricted: false,
});

const initialCommunicationData = cloneDeep(communicationData);

// functions

const loadAbTestTypes = async () => {
  status.spinnerMessage = 'Loading CRM A/B Test Types...';
  status.isLoading = true;
  try {
    crmAbTestTypes.value = await abTestService.fetchCrmAbTestTypes();
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    await showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const getExcludeAbTestOptions = () => {
  excludeAbTestTypes.value = [];
  noCrm.value = newPartnerInfo.value?.noCrm || false;

  if (!newPartnerInfo.value?.abTestOptions || !crmAbTestTypes.value) return;

  // Push test into the array if its API value is true.
  for (const abTestType of crmAbTestTypes.value) {
    if (newPartnerInfo.value.abTestOptions[abTestType] === true) {
      excludeAbTestTypes.value.push(abTestType);
    }
  }
  // Optionally, store the initial state
  initialExcludeAbTestTypes.value = cloneDeep(excludeAbTestTypes.value);
};

const transformAbTestDataForPayload = () => {
  const abData: any = {};
  for (const test of crmAbTestTypes.value) {
    abData[test] = excludeAbTestTypes.value.includes(test);
  }
  return abData;
};

const submitForm = async () => {
  status.isLoading = true;
  status.spinnerMessage = 'Updating Partner';
  const crmData = transformAbTestDataForPayload();

  const response = await updatePartner({
    codeName: newPartnerInfo.value?.codeName,
    data: {
      ...communicationData,
      abTestOptions: crmData,
      noCrm: noCrm.value.valueOf(),
    },
  }).catch((error) => {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
    status.isLoading = false;
  });

  if (!response) {
    return false;
  }

  setNewPartnerInfo({
    ...response,
    partnerHours: newPartnerInfo.value?.partnerHours || [],
    ...communicationData,
  });

  status.isLoading = false;
  await showSuccessPopupDialog({
    text: 'The communication info has been updated successfully.',
  });

  emit('goto-next-step');
};

const resetForm = () => {
  communicationData = initialCommunicationData;
  excludeAbTestTypes.value = initialExcludeAbTestTypes.value;
};

const populateCommunicationDataForUpdate = () => {
  if (newPartnerInfo.value) {
    for (const key in communicationData) {
      if (key in newPartnerInfo.value) {
        communicationData[key] = newPartnerInfo.value[key];
      }
    }
  }
};

const validateScheduledRideReminderMinutesAfterCreation = () => {
  invalidErrorMessage.value = scheduledRideReminderInputElement.value?.$el?.validationMessage;
};

// lifecycles
onMounted(async () => {
  await loadAbTestTypes();
  getExcludeAbTestOptions();
  populateCommunicationDataForUpdate();
});
</script>

<style scoped lang="scss">
.partner-communication {
  .checkbox-container {
    height: 50px;
  }
}
</style>
